<template>
  <div id="announcements" class="content-section">
    <div class="content-inner">
      <div class="section-title">
        <div class="title-main">ANNOUNCEMENTS</div>
        <div class="title-sub">官方公告 <span class="slash">//</span> ANNOUNCEMENTS</div>
      </div>

      <!-- 分类选项卡 -->
      <div v-if="!loading && categories.length > 0" class="anno-tabs">
        <button
          :class="['anno-tab', { active: activeCategory === 'all' }]"
          @click="activeCategory = 'all'"
        >全部</button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          :class="['anno-tab', { active: activeCategory === String(cat.id) }]"
          @click="activeCategory = String(cat.id)"
        >{{ cat.name }}</button>
      </div>

      <!-- 加载中 -->
      <div v-if="loading" class="anno-loading">
        <div class="loading-spinner"></div>
        <span>LOADING...</span>
      </div>

      <!-- 空状态 -->
      <div v-else-if="filteredList.length === 0" class="anno-empty">
        <div class="empty-code">// NO_DATA_FOUND</div>
        <p>暂无公告</p>
      </div>

      <!-- 公告卡片 -->
      <div v-else class="anno-grid">
        <div
          v-for="(item, idx) in filteredList"
          :key="item.id"
          class="tech-card anno-card"
          :style="{ '--accent': accentColor(idx) }"
          @click="openDetail(item, idx)"
        >
          <span class="card-bg-num">{{ String(idx + 1).padStart(2, '0') }}</span>
          <div class="card-header anno-card-header">
            <h3>{{ truncate(item.title) }}</h3>
          </div>
          <p v-if="item.desc" class="anno-desc">{{ item.desc }}</p>
          <div class="card-line"></div>
          <div class="card-footer">
            <span v-if="item.is_top" class="anno-pin">TOP</span>
            <span>{{ formatTime(item.created_at) }}</span>
            <span v-if="item.views !== undefined" class="anno-views">{{ formatViews(item.views) }} reads</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 详情弹窗 -->
  <Teleport to="body">
    <div v-if="modalVisible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-card" :style="{ '--modal-accent': modalAccent }">
        <div class="modal-header">
          <div class="modal-meta">
            <span v-if="currentDetail?.category_id" class="detail-category">{{ modalCategoryName }}</span>
            <span v-if="currentDetail?.is_top" class="anno-pin">TOP</span>
            <span class="detail-time">{{ formatTime(currentDetail?.created_at) }}</span>
          </div>
          <button class="modal-close" @click="closeModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <h2 class="modal-title">{{ currentDetail?.title }}</h2>
        <div class="modal-body" v-html="currentDetail?.content"></div>
        <!-- 加载中 -->
        <div v-if="detailLoading" class="modal-loading">
          <div class="loading-spinner"></div>
          <span>LOADING...</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getAnnouncementCategories, getAnnouncementList, getAnnouncementDetail } from '../api/index.js'

const list = ref([])
const categories = ref([])
const activeCategory = ref('all')
const loading = ref(true)

// 弹窗状态
const modalVisible = ref(false)
const currentDetail = ref(null)
const detailLoading = ref(false)
const modalAccent = ref('#e4467d')

const modalCategoryName = computed(() => {
  if (!currentDetail.value) return ''
  const cat = categories.value.find(c => String(c.id) === String(currentDetail.value.category_id))
  return cat?.name || '未分类'
})

const accentList = ['#00e5ff','#e4467d','#944bda','#ff9d00','#a855f7','#1abc9c']

function accentColor(idx) {
  return accentList[idx % accentList.length]
}

const filteredList = computed(() => {
  let items = list.value
  if (activeCategory.value !== 'all') {
    items = items.filter(item => String(item.category_id) === activeCategory.value)
  }
  return [...items].sort((a, b) => {
    if (a.is_top && !b.is_top) return -1
    if (!a.is_top && b.is_top) return 1
    return 0
  })
})

function formatTime(t) {
  if (!t) return ''
  return t.slice(0, 10)
}

function formatViews(v) {
  if (v >= 10000) return (v / 10000).toFixed(1) + 'w'
  return v
}

function truncate(str) {
  if (!str) return ''
  if (str.length <= 8) return str
  return str.slice(0, 8) + '...'
}

async function openDetail(item, idx) {
  modalVisible.value = true
  currentDetail.value = item
  modalAccent.value = accentColor(idx)
  detailLoading.value = true
  const detail = await getAnnouncementDetail(item.id)
  if (detail) currentDetail.value = detail
  detailLoading.value = false
}

function closeModal() {
  modalVisible.value = false
  currentDetail.value = null
}

onMounted(async () => {
  const [cats, result] = await Promise.all([
    getAnnouncementCategories(),
    getAnnouncementList({ limit: 50 }),
  ])
  if (Array.isArray(cats)) categories.value = cats
  if (result?.list) list.value = result.list
  loading.value = false
})
</script>

<style scoped>
/* 分类选项卡 */
.anno-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 40px;
}

.anno-tab {
  position: relative;
  padding: 10px 24px;
  font-family: var(--font-mono, 'Consolas', monospace);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(26,26,26,0.65);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: 0.2s;
}

/* 斜切边框：用伪元素画 */
.anno-tab::before {
  content: '';
  position: absolute;
  inset: 0;
  clip-path: polygon(8px 0, 100% 0, 100% 70%, calc(100% - 8px) 100%, 0 100%, 0 30%);
  background: rgba(0,0,0,0.16);
  z-index: -1;
  transition: 0.2s;
}

.anno-tab::after {
  content: '';
  position: absolute;
  inset: 1px;
  clip-path: polygon(8px 0, 100% 0, 100% 70%, calc(100% - 8px) 100%, 0 100%, 0 30%);
  background: #fff;
  z-index: -1;
  transition: 0.2s;
}

.anno-tab:hover::before {
  background: rgba(0,0,0,0.3);
}

.anno-tab.active::before {
  background: var(--text-main, #1a1a1a);
}

.anno-tab.active::after {
  background: var(--text-main, #1a1a1a);
}

.anno-tab.active {
  color: #fff;
}

/* 加载 / 空状态 */
.anno-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  gap: 12px;
  font-family: var(--font-mono, 'Consolas', monospace);
  font-size: 14px;
  color: rgba(0,0,0,0.3);
  letter-spacing: 2px;
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(0,0,0,0.06);
  border-top-color: var(--pink, #e4467d);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.anno-empty {
  text-align: center;
  padding: 40px 20px;
  font-family: var(--font-mono, 'Consolas', monospace);
  color: rgba(0,0,0,0.3);
}

.empty-code {
  font-size: 14px;
  letter-spacing: 2px;
  margin-bottom: 8px;
}

/* 公告卡片网格 */
.anno-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.anno-card {
  cursor: pointer;
}

.anno-card-header {
  padding: 35px 30px 0 30px;
  margin-bottom: 20px;
  padding-right: 70px;
}

.anno-card-header h3 {
  font-size: 20px;
  font-weight: 800;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.anno-desc {
  position: relative;
  z-index: 2;
  padding: 0 30px;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  text-align: justify;
  margin-bottom: 10px;
  min-height: 0;
}

.anno-pin {
  display: inline-block;
  background: var(--pink, #e4467d);
  color: #fff;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 1px;
  font-family: var(--font-mono, 'Consolas', monospace);
  margin-right: 8px;
  clip-path: polygon(4px 0, 100% 0, 100% 60%, calc(100% - 4px) 100%, 0 100%, 0 40%);
}

.anno-views {
  margin-left: 8px;
  font-family: var(--font-mono, 'Consolas', monospace);
}

/* ====== 详情弹窗 ====== */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.modal-card {
  position: relative;
  background: rgba(0, 0, 0, 0.1);
  padding: 1px;
  clip-path: polygon(0 0, 100% 0, 100% 93%, 94% 100%, 0 100%);
  width: 800px;
  max-width: 100%;
  max-height: 85vh;
  animation: slideUp 0.25s ease;
}
@keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

.modal-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: var(--modal-accent, #e4467d);
  opacity: 0.8;
  z-index: 3;
}

.modal-card::after {
  content: '';
  position: absolute;
  top: 1px; left: 1px; right: 1px; bottom: 1px;
  background: #ffffff;
  clip-path: polygon(0 0, 100% 0, 100% 93%, 94% 100%, 0 100%);
  z-index: 0;
}

.modal-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 30px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.modal-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.detail-category {
  font-family: var(--font-mono, 'Consolas', monospace);
  font-size: 12px;
  padding: 3px 12px;
  border-radius: 4px;
  border: 1px solid var(--modal-accent, #e4467d);
  color: var(--modal-accent, #e4467d);
  font-weight: 700;
  letter-spacing: 1px;
}

.detail-time {
  font-family: var(--font-mono, 'Consolas', monospace);
  font-size: 12px;
  color: rgba(26,26,26,0.5);
  letter-spacing: 1px;
}

.modal-close {
  background: transparent;
  border: 1px solid rgba(0,0,0,0.16);
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  color: rgba(26,26,26,0.65);
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--pink, #e4467d);
  color: #fff;
  border-color: var(--pink, #e4467d);
}

.modal-title {
  position: relative;
  z-index: 2;
  padding: 20px 30px;
  font-size: 24px;
  font-weight: 800;
  font-style: italic;
  color: var(--text-main, #1a1a1a);
  margin: 0;
}

.modal-body {
  position: relative;
  z-index: 2;
  padding: 10px 30px 40px;
  overflow-y: auto;
  max-height: calc(85vh - 140px);
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.modal-body :deep(h1),
.modal-body :deep(h2),
.modal-body :deep(h3) {
  font-weight: 800;
  font-style: italic;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  color: var(--text-main, #1a1a1a);
}

.modal-body :deep(p) {
  margin: 0 0 1em;
}

.modal-body :deep(img) {
  max-width: 100%;
  border-radius: 8px;
}

.modal-body :deep(a) {
  color: var(--pink, #e4467d);
  text-decoration: none;
  border-bottom: 1px solid rgba(228, 70, 125, 0.3);
  transition: 0.2s;
}

.modal-body :deep(a:hover) {
  border-color: var(--pink, #e4467d);
}

.modal-body :deep(ul), .modal-body :deep(ol) {
  padding-left: 1.5em;
}

.modal-body :deep(blockquote) {
  border-left: 4px solid var(--pink, #e4467d);
  margin: 1em 0;
  padding: 12px 20px;
  background: rgba(228, 70, 125, 0.06);
  color: #555;
  font-style: italic;
}

.modal-body :deep(code) {
  background: rgba(0,0,0,0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: var(--font-mono, 'Consolas', monospace);
  font-size: 14px;
}

.modal-body :deep(pre) {
  background: #1a1a2e;
  padding: 20px;
  border-radius: 8px;
  overflow-x: auto;
}

.modal-body :deep(pre code) {
  background: transparent;
  color: #e0e0e0;
}

.modal-loading {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  gap: 10px;
  font-family: var(--font-mono, 'Consolas', monospace);
  font-size: 13px;
  color: rgba(0,0,0,0.3);
  letter-spacing: 2px;
}

/* 响应式 */
@media (max-width: 900px) {
  .anno-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .modal-card {
    width: 95%;
  }
}

@media (max-width: 768px) {
  .anno-grid {
    grid-template-columns: 1fr;
  }
  .anno-tabs {
    gap: 8px;
  }
  .anno-tab {
    padding: 6px 14px;
    font-size: 12px;
  }
  .modal-body {
    padding: 10px 20px 30px;
  }
  .modal-title {
    padding: 16px 20px;
    font-size: 20px;
  }
  .modal-header {
    padding: 16px 20px 12px;
  }
}

@media (max-width: 480px) {
  .modal-card {
    width: 100%;
    max-height: 90vh;
  }
}
</style>
